import { defineStore } from 'pinia'
import { ref } from 'vue'

const originKey = 'order.origin'
const selectionKey = 'order.selection'
const searchInputKey = 'order.searchInput'
const existingCustomerKey = 'order.existingCustomer'
const verificationDataKey = 'order.verificationData'
const changeRateDataKey = 'order.changeRate.contractData'
const simulationModeKey = 'order.simulationMode'

const _readSessionStorage = (key, fallback, parseJson = true) => {
  const selection = sessionStorage.getItem(key)

  if (!selection) {
    return fallback ?? null
  }

  if (!parseJson) {
    return selection
  }

  try {
    return JSON.parse(selection)
  } catch {
    sessionStorage.removeItem(key)
    return fallback ?? null
  }
}

const _saveToSessionStorage = (key, data) => {
  if (data === null || data === undefined) {
    sessionStorage.removeItem(key)
    return
  }

  if (data instanceof Object) {
    data = JSON.stringify(data)
  } else {
    data = data.toString()
  }

  sessionStorage.setItem(key, data)
}

export const useOrderStore = defineStore('orders', () => {
  const origin = ref(_readSessionStorage(originKey, 'DIGITAL_SALES', false))
  const selection = ref(_readSessionStorage(selectionKey))
  const searchInput = ref(_readSessionStorage(searchInputKey))
  const existingCustomer = ref(null)
  const verificationData = ref(_readSessionStorage(verificationDataKey))
  const changeRateData = ref(_readSessionStorage(changeRateDataKey))
  const simulationMode = ref(_readSessionStorage(simulationModeKey, false, false) === 'true')

  const clearOrders = () => {
    selection.value = null
    searchInput.value = null
    const clearKeys = [selectionKey, existingCustomerKey, verificationDataKey, changeRateDataKey]
    clearKeys.forEach(key => _saveToSessionStorage(key, null))
  }

  const setRefAndSave = (ref, key, value, saveValue = undefined) => {
    ref.value = value
    _saveToSessionStorage(key, saveValue ?? ref.value)
  }

  const setOrderOrigin = o => {
    if (o !== 'PORTAL' && o !== 'DIGITAL_SALES') {
      throw new Error(`Invalid value for order origin (${o})`)
    }
    setRefAndSave(origin, originKey, o)
  }

  const saveSearchInput = si => setRefAndSave(searchInput, searchInputKey, si)
  const selectProduct = searchResult => setRefAndSave(selection, selectionKey, searchResult)
  const saveVerificationData = vd => setRefAndSave(verificationData, verificationDataKey, vd)
  const saveChangeRateData = crd => setRefAndSave(changeRateData, changeRateDataKey, crd)
  const revokeAccessGranted = () => setRefAndSave(existingCustomer, existingCustomerKey, { accessGranted: false }, null)
  const enterSimulationMode = () => setRefAndSave(simulationMode, simulationModeKey, true)

  const exitSimulationMode = () => {
    setRefAndSave(simulationMode, simulationModeKey, false, null)
    clearOrders()
  }

  const setAccessGranted = () => {
    const granted = { accessGranted: true }
    existingCustomer.value = granted
  }

  return {
    origin,
    selection,
    searchInput,
    existingCustomer,
    verificationData,
    changeRateData,
    simulationMode,
    clearOrders,
    setOrderOrigin,
    saveSearchInput,
    selectProduct,
    setAccessGranted,
    saveVerificationData,
    saveChangeRateData,
    revokeAccessGranted,
    enterSimulationMode,
    exitSimulationMode
  }
})
